import { AbilityService } from 'ability';
import Computer, { AgentType } from 'mbs-ui-shared@models/Computer';
import { ComputerLicenseType } from 'mbs-ui-shared@models/License';
import { PermissionsEnum } from 'mbs-ui-shared@models/PermissionsEnum';
import { canSeeDocuments } from 'mbs-ui-shared@utils/documents/documents.permissions';

export enum RMMSidePanelTab {
  general = 'general',
  tickets = 'tickets',
  documents = 'documents',
  license = 'license',
  pcInfo = 'pc-info',
  notes = 'notes',
  processes = 'processes',
  processesCharts = 'processesCharts',
  processesGrid = 'processesGrid',
  services = 'services',
  softwareInfo = 'softwareInfo',
  software = 'software',
  availableHotfixes = 'availableHotfixes',
  installedHotfixes = 'installedHotfixes',
  hardwareInfo = 'hardwareInfo',
  disk = 'disk',
  hardware = 'hardware',
  printers = 'printers',
  event = 'event',
  terminal = 'terminal',
  operations = 'operations',
  antivirus = 'antivirus',
  hyperv = 'hyperv',
  remoteRegistry = 'remote-registry'
}

export const hasRmmLicense = (computer: Computer, ability: AbilityService): boolean => {
  const rmmPE = ability.can('read', PermissionsEnum.RmmPerEndpoint);

  return (
    (rmmPE && Computer.license(computer, ComputerLicenseType.RMM).hasLicense()) ||
    (!rmmPE && ability.can('read', PermissionsEnum.RmmPerAdminLicenseExists))
  );
};

export const enabledRMMSidePanelTabs = (computer: Computer, ability: AbilityService) => {
  const computerLinkedToCompany = !!computer?.company?.id;
  const hasRmmAgent = Computer.hasAgent(computer, AgentType.RMM);
  const isOnlineAgent = Computer.isAgentOnline(computer, AgentType.RMM);
  const isWindows = Computer.isWindows(computer);
  const showTickets = ability.can('read', PermissionsEnum.AllowTicketingSubsystem) && ability.can('read', PermissionsEnum.ShowTickets);
  const showDocuments = canSeeDocuments(ability);
  const rmmPE = ability.can('read', PermissionsEnum.RmmPerEndpoint);
  const hasLicense = hasRmmLicense(computer, ability);

  const rules: { tab: RMMSidePanelTab; visible: boolean; enabled: boolean }[] = [
    { tab: RMMSidePanelTab.general, visible: true, enabled: hasLicense && hasRmmAgent },
    { tab: RMMSidePanelTab.tickets, visible: showTickets, enabled: hasLicense && computerLinkedToCompany && showTickets },
    { tab: RMMSidePanelTab.documents, visible: showDocuments, enabled: hasLicense && computerLinkedToCompany && showDocuments },
    { tab: RMMSidePanelTab.license, visible: rmmPE, enabled: rmmPE },
    { tab: RMMSidePanelTab.pcInfo, visible: true, enabled: hasLicense && hasRmmAgent },
    { tab: RMMSidePanelTab.processes, visible: true, enabled: hasLicense && hasRmmAgent },
    { tab: RMMSidePanelTab.processesCharts, visible: isOnlineAgent, enabled: hasLicense && hasRmmAgent && isOnlineAgent },
    { tab: RMMSidePanelTab.processesGrid, visible: true, enabled: hasLicense && hasRmmAgent },
    { tab: RMMSidePanelTab.services, visible: true, enabled: hasLicense && hasRmmAgent },
    { tab: RMMSidePanelTab.softwareInfo, visible: true, enabled: hasLicense && hasRmmAgent },
    { tab: RMMSidePanelTab.software, visible: true, enabled: hasLicense && hasRmmAgent },
    { tab: RMMSidePanelTab.availableHotfixes, visible: true, enabled: hasLicense && hasRmmAgent },
    { tab: RMMSidePanelTab.installedHotfixes, visible: true, enabled: hasLicense && hasRmmAgent },
    { tab: RMMSidePanelTab.hardwareInfo, visible: true, enabled: hasLicense && hasRmmAgent },
    { tab: RMMSidePanelTab.disk, visible: true, enabled: hasLicense && hasRmmAgent },
    { tab: RMMSidePanelTab.hardware, visible: isWindows, enabled: hasLicense && isWindows && hasRmmAgent },
    { tab: RMMSidePanelTab.printers, visible: true, enabled: hasLicense && hasRmmAgent },
    { tab: RMMSidePanelTab.event, visible: true, enabled: hasLicense && hasRmmAgent },
    { tab: RMMSidePanelTab.terminal, visible: isOnlineAgent, enabled: hasLicense && isOnlineAgent },
    { tab: RMMSidePanelTab.operations, visible: isWindows, enabled: hasLicense && isWindows && hasRmmAgent },
    { tab: RMMSidePanelTab.antivirus, visible: isWindows, enabled: hasLicense && isWindows && hasRmmAgent },
    { tab: RMMSidePanelTab.hyperv, visible: isWindows, enabled: hasLicense && isWindows && hasRmmAgent },
    { tab: RMMSidePanelTab.remoteRegistry, visible: isWindows && isOnlineAgent, enabled: hasLicense && isOnlineAgent && isWindows }
  ];

  return rules;
};
